import { Box, Typography } from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import clsx from "clsx";
import moment from "moment";
import PropTypes from "prop-types";
import * as React from "react";
import { InfluencerEvent } from "../../domain";
import { useMatchesSmDown } from "../../hooks";
import { Hint, MessageDialog } from "../../theme";
import { limitLength } from "../../util";

const baseRowStyles = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  "& .event-icon": {
    fontSize: theme.typography.pxToRem(14),
    flexShrink: 0,
  },
  "&.right": {
    textAlign: "right",
    flexDirection: "row-reverse",
  },
  "&.left": {
    textAlign: "left",
  },
});

const LiveStyledButton = styled("button")(({ theme }) => ({
  ...baseRowStyles(theme),
  ...theme.typography.body2,
  fontWeight: theme.typography.bold,
  fontSize: theme.typography.pxToRem(15),
  color: theme.palette.text.primary,
  padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
  minHeight: 44,
  border: 0,
  cursor: "pointer",
  transition: "all 0.1s ease-out",
  background: "none",
  lineHeight: 1.75,
  "& .event-icon": {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.primary.main,
  },
  "&:hover .event-label, &:focus-visible .event-label": {
    textDecoration: "underline",
  },
  "&:hover": {
    background: "none",
  },
}));

const NonLiveStyledButton = styled("button")(({ theme }) => ({
  ...baseRowStyles(theme),
  ...theme.typography.body2,
  fontWeight: theme.typography.normal,
  fontSize: theme.typography.pxToRem(13),
  color: theme.palette.grey.midDark || theme.palette.text.secondary,
  padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
  minHeight: 44,
  border: 0,
  cursor: "pointer",
  transition: "all 0.1s ease-out",
  background: "none",
  lineHeight: 1.6,
  "& .event-icon": {
    color: theme.palette.grey.mid,
  },
  "&:hover .event-label, &:focus-visible .event-label": {
    textDecoration: "underline",
  },
  "&:hover": {
    background: "none",
  },
}));

const StyledSpan = styled("span")(() => ({
  color: "#2b2b2b",
  fontWeight: "bold",
}));

const StyledLink = styled("a")(({ theme }) => ({
  "&.left": {
    textAlign: "left",
  },
  "&.right": {
    textAlign: "right",
  },
  fontWeight: theme.typography.bold,
  padding: 0,
  margin: 0,
  border: 0,
  color: "#000",
  cursor: "pointer",
  transition: "all 0.1s ease-out",
  background: "none",
  textDecoration: "underline",
  "&:hover": {
    textDecoration: "underline",
    background: "none",
  },
}));

const LiveStyledText = styled("span")(({ theme }) => ({
  ...baseRowStyles(theme),
  ...theme.typography.body2,
  fontWeight: theme.typography.bold,
  fontSize: theme.typography.pxToRem(15),
  color: theme.palette.text.primary,
  padding: 0,
  margin: 0,
  border: 0,
  lineHeight: 1.75,
  "& .event-icon": {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.primary.main,
  },
}));

const NonLiveStyledText = styled("span")(({ theme }) => ({
  ...baseRowStyles(theme),
  ...theme.typography.body2,
  fontWeight: theme.typography.normal,
  fontSize: theme.typography.pxToRem(13),
  color: theme.palette.grey.midDark || theme.palette.text.secondary,
  padding: 0,
  margin: 0,
  border: 0,
  lineHeight: 1.6,
  "& .event-icon": {
    color: theme.palette.grey.mid,
  },
}));

const withSuffix = (influencerEvent) => {
  const platformSuffix = influencerEvent?.platform
    ? ` on ${influencerEvent.platform}`
    : "";
  const influencerSuffix = influencerEvent.hasInfluencer
    ? ` with ${influencerEvent.influencerName}`
    : "";
  return `${platformSuffix}${influencerSuffix}`;
};

const buildEditLabel = (influencerEvent, isNonLive) => {
  const prefix = isNonLive ? "Non-live order: " : "";
  return `${prefix}${influencerEvent.formattedValue}${withSuffix(
    influencerEvent,
  )}`;
};

const buildTitledLabel = (influencerEvent, isNonLive) => {
  if (isNonLive) {
    return `${influencerEvent.formattedTitleWithDateValue}${
      influencerEvent.hasInfluencer
        ? ` with ${influencerEvent.influencerName}`
        : ""
    }`;
  }
  return `${limitLength(influencerEvent.eventTitle, 30)} - ${
    influencerEvent.formattedTitleWithDateValue
  }${
    influencerEvent.hasInfluencer
      ? ` with ${influencerEvent.influencerName}`
      : ""
  }`;
};

const buildFallbackLabel = (influencerEvent) =>
  `${influencerEvent.formattedValue}${withSuffix(influencerEvent)}`;

const InfluencerEventElement = React.memo(function InfluencerEvent({
  influencerEvent,
  align = "left",
  onClick,
  variant = "live",
}) {
  const isMobile = useMatchesSmDown();
  const isNonLive = variant === "nonLive";

  const [hasEventData] = React.useState(
    influencerEvent.eventType && influencerEvent.eventTitle,
  );
  const [showEventDetails, setShowEventDetails] = React.useState(false);

  const makeDateDetails = React.useCallback(() => {
    if (influencerEvent.startDate) {
      return (
        <>
          <Box style={{ marginBottom: "0.3rem" }}>
            <Typography>
              <StyledSpan>From: </StyledSpan>
              {moment(influencerEvent.startDate).format("MMMM Do, YYYY")}
            </Typography>
            <Typography></Typography>
          </Box>
          <Box style={{ marginBottom: "0.3rem" }}>
            <Typography>
              <StyledSpan>To: </StyledSpan>
              {moment(influencerEvent.endDate).format("MMMM Do, YYYY")}
            </Typography>
          </Box>
        </>
      );
    }
    return (
      <>
        <Box style={{ marginBottom: "0.3rem" }}>
          <Typography>
            <StyledSpan>Date: </StyledSpan>
            {moment(influencerEvent.value).format("MMMM Do, YYYY")}
          </Typography>
        </Box>
        {influencerEvent.hasTime && (
          <Box style={{ marginBottom: "0.3rem" }}>
            <Typography>
              <StyledSpan>Time: </StyledSpan>
              {moment(influencerEvent.value).format("h:mm A ")}{" "}
              {influencerEvent.browserTimezone}
            </Typography>
          </Box>
        )}
      </>
    );
  }, [influencerEvent]);

  const makeLocation = React.useCallback(() => {
    if (influencerEvent.url) {
      return (
        <StyledLink
          href={influencerEvent.url}
          target="_blank"
          rel="noopener noreferrer"
          title={influencerEvent.eventUrl}
        >
          {influencerEvent.platform}
        </StyledLink>
      );
    }
    return influencerEvent.platform;
  }, [influencerEvent]);

  const handleEventType = () => {
    if (showEventDetails) return;
    setShowEventDetails(true);
  };

  const ButtonComponent = isNonLive ? NonLiveStyledButton : LiveStyledButton;
  const TextComponent = isNonLive ? NonLiveStyledText : LiveStyledText;

  // The onClick function is only passed in when the event is on the shop
  // builder page, and the user is allowed to edit the event. In this case, it
  // will open the event dialog to edit the event.
  if (onClick) {
    const editLabel = buildEditLabel(influencerEvent, isNonLive);
    return (
      <ButtonComponent
        className={clsx(align)}
        variant="text"
        color="default"
        haseventtype={"false"}
        onClick={onClick}
        aria-label={isNonLive ? `${editLabel}. Not a livestream.` : undefined}
      >
        <span className="event-label">{editLabel}</span>
      </ButtonComponent>
    );
  }

  // show popup of event details: title, date, platform, description
  if (hasEventData) {
    const label = buildTitledLabel(influencerEvent, isNonLive);

    return (
      <>
        <ButtonComponent
          className={clsx(align)}
          variant="text"
          color="default"
          haseventtype={String(!!influencerEvent.eventType)}
          onClick={handleEventType}
          aria-label={
            isNonLive ? `${label}. Not a livestream. Open details.` : undefined
          }
        >
          <span className="event-label">{label}</span>
        </ButtonComponent>

        {showEventDetails && (
          <MessageDialog
            title={influencerEvent.eventTitle}
            message={
              <Box style={isMobile ? {} : { minWidth: "30rem" }}>
                {isNonLive && (
                  <Box style={{ marginBottom: "0.6rem" }}>
                    <Typography>
                      <StyledSpan>This is not a livestream.</StyledSpan> Orders
                      are fulfilled off-camera as a batch during the dates
                      shown.
                    </Typography>
                  </Box>
                )}
                {makeDateDetails()}
                {influencerEvent.platform && (
                  <Box style={{ marginBottom: "0.3rem" }}>
                    <Typography>
                      {" "}
                      <StyledSpan>Location: </StyledSpan>
                      {makeLocation()}
                    </Typography>
                  </Box>
                )}

                {influencerEvent.eventDescription && (
                  <Typography>
                    <StyledSpan>Event Description: </StyledSpan>
                    {influencerEvent.eventDescription}
                  </Typography>
                )}

                {influencerEvent.printBoxDisclaimer && (
                  <Hint>{influencerEvent.printBoxDisclaimer}</Hint>
                )}
              </Box>
            }
            onClose={() => {
              setShowEventDetails(false);
            }}
          />
        )}
      </>
    );
  }

  const fallbackLabel = buildFallbackLabel(influencerEvent);

  return (
    <TextComponent
      className={align}
      aria-label={
        isNonLive
          ? `Non-live order: ${fallbackLabel}. Not a livestream.`
          : undefined
      }
    >
      <span className="event-label">{fallbackLabel}</span>
    </TextComponent>
  );
});

InfluencerEventElement.propTypes = {
  influencerEvent: PropTypes.instanceOf(InfluencerEvent),
  align: PropTypes.oneOf(["left", "right"]),
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(["live", "nonLive"]),
};

export default InfluencerEventElement;
